import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { GroupSenderName, GroupSenderNameCreate, GroupSenderNameUpdate } from '../types';

export function createGroupSenderNamesAdapter(
  routingChoiceId: number,
): CrudAdapter<GroupSenderName, GroupSenderNameCreate, GroupSenderNameUpdate> {
  return {
    async list({ offset, limit, sortName, sortDirection }) {
      const { data, error } = await smsClient.GET(
        '/routing_choices/{routing_choice_id}/group_sender_names/',
        {
          params: {
            path: { routing_choice_id: routingChoiceId },
            query: {
              offset,
              limit,
              'sort.name': sortName ?? 'id',
              'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
            },
          },
        },
      );
      if (error) throw error;
      return { items: data?.items ?? [], total: data?.total ?? 0 };
    },
    async create(payload) {
      const body: GroupSenderNameCreate = {
        ...payload,
        routing_choice_id: routingChoiceId,
      };
      const { data, error } = await smsClient.POST(
        '/routing_choices/{routing_choice_id}/group_sender_names/',
        {
          params: { path: { routing_choice_id: routingChoiceId } },
          body,
        },
      );
      if (error) throw error;
      return { id: data?.id ?? 0 };
    },
    async update(payload) {
      const { data, error } = await smsClient.PUT(
        '/routing_choices/{routing_choice_id}/group_sender_names/',
        {
          params: { path: { routing_choice_id: routingChoiceId } },
          body: { ...payload, routing_choice_id: routingChoiceId },
        },
      );
      if (error) throw error;
      return (data ?? payload) as GroupSenderName;
    },
    async byKey(id) {
      const { data, error } = await smsClient.GET(
        '/routing_choices/{routing_choice_id}/group_sender_names/{item_id}',
        {
          params: {
            path: { routing_choice_id: routingChoiceId, item_id: Number(id) },
          },
        },
      );
      if (error) throw error;
      return data as GroupSenderName;
    },
    async remove(id) {
      const { error } = await smsClient.DELETE(
        '/routing_choices/{routing_choice_id}/group_sender_names/{item_id}',
        {
          params: {
            path: { routing_choice_id: routingChoiceId, item_id: Number(id) },
          },
        },
      );
      if (error) throw error;
    },
  };
}

export function createGroupSenderNamesStore(routingChoiceId: number) {
  return useCrudDataSource(createGroupSenderNamesAdapter(routingChoiceId));
}
